/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
    FPSectionCard, FPSectionHeader, FPSectionBody,
    FPMethodGrid, FPMethodCard, FPMethodName, FPMethodMeta,
    FPCategoryHeader,
    FPEligibilityPanel, FPEligibilityHeader, FPEligibilityBody,
    FPEligibilityList, FPEligibilityLabel,
    FPEmptyState, FPEmptyIcon, FPEmptyTitle, FPEmptyDesc,
    FPAlertBanner, FPAlertIcon, FPAlertContent, FPAlertTitle, FPAlertDetail,
} from "./FamilyPlanningStyles";

/* ═══════════════════════════════════════════════════════════════
   FPMethodEligibility — Method eligibility guidance (Task 17)
   Interactive method browser with WHO MEC criteria displayed
   for each family planning method available in Uganda.
   ═══════════════════════════════════════════════════════════════ */

export const FPMethodEligibility = function (props) {
    const fp = props.fp;

    /* ─── Selected method for detail view ─── */
    const selState = useState(null);
    const selectedMethod = selState[0];
    const setSelectedMethod = selState[1];

    /* ─── Group methods by category ─── */
    const methodsByCategory = {};
    fp.FP_METHOD_CATEGORIES.forEach(function (cat) {
        methodsByCategory[cat.value] = fp.FP_METHODS.filter(function (m) { return m.category === cat.value; });
    });

    const selectedInfo = selectedMethod
        ? fp.FP_METHODS.find(function (m) { return m.value === selectedMethod; })
        : null;
    const selectedCat = selectedInfo
        ? fp.FP_METHOD_CATEGORIES.find(function (c) { return c.value === selectedInfo.category; })
        : null;
    const mec = selectedMethod ? (fp.MEC_CATEGORIES[selectedMethod] || null) : null;

    return (
        <div>
            {/* ─── Info Banner ─── */}
            <FPAlertBanner type="info">
                <FPAlertIcon>📖</FPAlertIcon>
                <FPAlertContent>
                    <FPAlertTitle>WHO Medical Eligibility Criteria (MEC) Reference</FPAlertTitle>
                    <FPAlertDetail>
                        Click on any method to view contraindications and precautions based on WHO guidelines.
                        This is a clinical decision support tool — always exercise clinical judgment.
                    </FPAlertDetail>
                </FPAlertContent>
            </FPAlertBanner>

            {/* ─── Method Browser ─── */}
            <FPSectionCard>
                <FPSectionHeader color="#1565c0">
                    🛡️ Available Family Planning Methods
                </FPSectionHeader>
                <FPSectionBody>
                    {fp.FP_METHOD_CATEGORIES.map(function (cat) {
                        const methods = methodsByCategory[cat.value];
                        if (!methods || methods.length === 0) return null;
                        return (
                            <div key={cat.value}>
                                <FPCategoryHeader color={cat.color}>
                                    <span>{cat.icon}</span>
                                    <span>{cat.label}</span>
                                </FPCategoryHeader>
                                <FPMethodGrid>
                                    {methods.map(function (m) {
                                        const isSelected = selectedMethod === m.value;
                                        const hasMec = !!fp.MEC_CATEGORIES[m.value];
                                        return (
                                            <FPMethodCard
                                                key={m.value}
                                                selected={isSelected}
                                                categoryColor={cat.color}
                                                onClick={function () {
                                                    setSelectedMethod(isSelected ? null : m.value);
                                                }}
                                            >
                                                <FPMethodName>{m.label}</FPMethodName>
                                                <FPMethodMeta>
                                                    {m.renewalMonths
                                                        ? "Duration: " + (m.renewalMonths >= 12 ? (m.renewalMonths / 12) + " year(s)" : m.renewalMonths + " month(s)")
                                                        : "Ongoing / Per use"
                                                    }
                                                </FPMethodMeta>
                                                {m.requiresBP && (
                                                    <FPMethodMeta style={{ color: "#e65100" }}>⚡ BP check required</FPMethodMeta>
                                                )}
                                                {hasMec && (
                                                    <FPMethodMeta style={{ color: "#1565c0" }}>
                                                        📋 MEC criteria available
                                                    </FPMethodMeta>
                                                )}
                                            </FPMethodCard>
                                        );
                                    })}
                                </FPMethodGrid>
                            </div>
                        );
                    })}
                </FPSectionBody>
            </FPSectionCard>

            {/* ─── Selected Method Details ─── */}
            {selectedMethod && selectedInfo && (
                <FPEligibilityPanel>
                    <FPEligibilityHeader>
                        <span>{selectedCat ? selectedCat.icon : "📋"} {selectedInfo.label} — Eligibility Details</span>
                        <span style={{ cursor: "pointer", fontSize: "0.85rem" }}
                            onClick={function () { setSelectedMethod(null); }}>
                            ✕ Close
                        </span>
                    </FPEligibilityHeader>
                    <FPEligibilityBody>
                        {/* Method summary */}
                        <div style={{
                            padding: "12px 16px",
                            background: "#f5f5f5",
                            borderRadius: "8px",
                            marginBottom: "16px",
                            fontSize: "0.85rem",
                            color: "#333",
                        }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                                <div><strong>Category:</strong> {selectedCat ? selectedCat.label : "N/A"}</div>
                                <div><strong>Duration:</strong> {selectedInfo.renewalMonths
                                    ? (selectedInfo.renewalMonths >= 12 ? (selectedInfo.renewalMonths / 12) + " year(s)" : selectedInfo.renewalMonths + " month(s)")
                                    : "Ongoing / Per use"
                                }</div>
                                <div><strong>BP Required:</strong> {selectedInfo.requiresBP ? "Yes" : "No"}</div>
                                <div><strong>Type:</strong> {
                                    selectedInfo.category === "permanent" ? "Irreversible" : "Reversible"
                                }</div>
                            </div>
                        </div>

                        {mec ? (
                            <div>
                                {mec.contraindications.length > 0 && (
                                    <div>
                                        <FPEligibilityLabel type="contraindication">
                                            🚫 Absolute Contraindications (WHO MEC Category 3–4)
                                        </FPEligibilityLabel>
                                        <div style={{ fontSize: "0.78rem", color: "#888", marginBottom: "4px" }}>
                                            Method should NOT be used if any of these conditions apply:
                                        </div>
                                        <FPEligibilityList>
                                            {mec.contraindications.map(function (ci, i) {
                                                return <li key={i} style={{ color: "#c62828" }}>{ci}</li>;
                                            })}
                                        </FPEligibilityList>
                                    </div>
                                )}

                                {mec.precautions.length > 0 && (
                                    <div>
                                        <FPEligibilityLabel type="precaution">
                                            ⚠️ Precautions / Relative Contraindications (WHO MEC Category 2–3)
                                        </FPEligibilityLabel>
                                        <div style={{ fontSize: "0.78rem", color: "#888", marginBottom: "4px" }}>
                                            Method use requires careful clinical assessment if any apply:
                                        </div>
                                        <FPEligibilityList>
                                            {mec.precautions.map(function (p, i) {
                                                return <li key={i} style={{ color: "#e65100" }}>{p}</li>;
                                            })}
                                        </FPEligibilityList>
                                    </div>
                                )}

                                {mec.contraindications.length === 0 && mec.precautions.length === 0 && (
                                    <FPAlertBanner type="success">
                                        <FPAlertIcon>✅</FPAlertIcon>
                                        <FPAlertContent>
                                            <FPAlertTitle>No major contraindications listed</FPAlertTitle>
                                            <FPAlertDetail>
                                                This method has no common absolute contraindications. Always verify individual patient history.
                                            </FPAlertDetail>
                                        </FPAlertContent>
                                    </FPAlertBanner>
                                )}
                            </div>
                        ) : (
                            <FPAlertBanner type="info">
                                <FPAlertIcon>ℹ️</FPAlertIcon>
                                <FPAlertContent>
                                    <FPAlertTitle>MEC data not available</FPAlertTitle>
                                    <FPAlertDetail>
                                        WHO Medical Eligibility Criteria data is not loaded for this method.
                                        Refer to WHO MEC guidelines for detailed eligibility assessment.
                                    </FPAlertDetail>
                                </FPAlertContent>
                            </FPAlertBanner>
                        )}

                        {/* Practical notes */}
                        <div style={{
                            marginTop: "16px",
                            padding: "12px 16px",
                            background: "#e8f5e9",
                            borderRadius: "8px",
                            border: "1px solid #a5d6a7",
                            fontSize: "0.82rem",
                            color: "#2e7d32",
                        }}>
                            <strong>💡 Clinical Reminder:</strong>{" "}
                            {selectedInfo.category === "larc" && "Long-acting methods require trained provider for insertion/removal. Ensure follow-up scheduling."}
                            {selectedInfo.category === "short_acting" && "Client should be counselled on consistent daily/regular use. Provide return date."}
                            {selectedInfo.category === "barrier" && "Dual protection against STIs and pregnancy. Counsel on correct use with every act of intercourse."}
                            {selectedInfo.category === "permanent" && "Permanent method — ensure thorough counselling and informed consent. Irreversible."}
                            {selectedInfo.category === "natural" && "Requires client understanding and commitment. May not be suitable for all women."}
                            {selectedInfo.category === "emergency" && "Emergency use only. Provide within 72–120 hours. Counsel on regular method initiation."}
                        </div>
                    </FPEligibilityBody>
                </FPEligibilityPanel>
            )}

            {/* ─── Quick Reference: MEC Category Explanation ─── */}
            <FPSectionCard>
                <FPSectionHeader color="#00695c">
                    📚 WHO MEC Quick Reference
                </FPSectionHeader>
                <FPSectionBody>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                        gap: "10px",
                    }}>
                        <div style={{ padding: "12px", background: "#e8f5e9", borderRadius: "8px", border: "1px solid #a5d6a7" }}>
                            <div style={{ fontWeight: 700, color: "#2e7d32", fontSize: "0.85rem" }}>Category 1</div>
                            <div style={{ fontSize: "0.8rem", color: "#333" }}>No restriction — Method can be used</div>
                        </div>
                        <div style={{ padding: "12px", background: "#e3f2fd", borderRadius: "8px", border: "1px solid #90caf9" }}>
                            <div style={{ fontWeight: 700, color: "#1565c0", fontSize: "0.85rem" }}>Category 2</div>
                            <div style={{ fontSize: "0.8rem", color: "#333" }}>Advantages generally outweigh risks</div>
                        </div>
                        <div style={{ padding: "12px", background: "#fff8e1", borderRadius: "8px", border: "1px solid #ffe082" }}>
                            <div style={{ fontWeight: 700, color: "#e65100", fontSize: "0.85rem" }}>Category 3</div>
                            <div style={{ fontSize: "0.8rem", color: "#333" }}>Risks generally outweigh advantages — not usually recommended</div>
                        </div>
                        <div style={{ padding: "12px", background: "#ffebee", borderRadius: "8px", border: "1px solid #ef9a9a" }}>
                            <div style={{ fontWeight: 700, color: "#c62828", fontSize: "0.85rem" }}>Category 4</div>
                            <div style={{ fontSize: "0.8rem", color: "#333" }}>Unacceptable health risk — method should NOT be used</div>
                        </div>
                    </div>
                </FPSectionBody>
            </FPSectionCard>
        </div>
    );
};
